/* Silver Gardens bundle builder. Native parent bundle + linked physical pots. */
(() => {
  'use strict';

  if (customElements.get('sg-bundle-builder')) return;

  const normal = value => String(value || '').trim().toLowerCase();
  const isNursery = value => normal(value).includes('nursery');
  const potName = (plant, variant) =>
    String(variant.options[plant.potIndex] || 'Pot');
  const colourName = (plant, variant) =>
    String(variant.options[plant.colourIndex] || '');

  function eligible(plant, variant, config) {
    if (!variant.available) return false;

    if (!variant.options.every((value, index) =>
      index === plant.potIndex ||
      index === plant.colourIndex ||
      value === plant.fixedOptions[index]
    )) return false;

    if (isNursery(potName(plant, variant))) {
      return !config.hideNursery;
    }

    return Boolean(variant.potId && variant.potAvailable);
  }

  function choose(plant, variants, config) {
    return variants.find(v => v.id === plant.defaultId) ||
      variants.find(v =>
        normal(potName(plant, v)) === normal(config.preferredPot) &&
        normal(colourName(plant, v)) === normal(config.preferredColour)
      ) ||
      variants.find(v =>
        normal(potName(plant, v)) === normal(config.preferredPot)
      ) ||
      variants.find(v =>
        normal(colourName(plant, v)) === normal(config.preferredColour)
      ) ||
      variants[0] ||
      null;
  }

  function totals(bundle, selections, quantity, pebbles, plants = [], config = {}) {
    const potTotal = selections.reduce(
      (sum, variant) =>
        sum + (variant ? Number(variant.potPrice || 0) : 0),
      0
    );

    const plantsTotal = selections.reduce(
      (sum, variant) =>
        sum + (variant ? Number(variant.price || 0) : 0),
      0
    );

    const finish = pebbles.id && pebbles.quantity > 0
      ? pebbles.price * pebbles.quantity
      : 0;

    // Shopify's Buy-Pots/Get-Bundle pot credit cancels the default
    // included pot value at checkout. Keep this identical in the UI.
    const includedCredit = config.nativeInclusiveBundle
      ? Number(config.includedPotCredit || 0)
      : 0;
    const total =
      (Number(bundle.price) + potTotal - includedCredit) * quantity + finish;

    const individualPotTotal = selections.reduce(
      (sum, variant, index) => sum + (
        plants[index]?.inclusivePricing ? 0 : Number(variant?.potPrice || 0)
      ), 0);
    const individual =
      (plantsTotal + individualPotTotal) * quantity + finish;

    const saving = Math.max(0, individual - total);

    return {
      total,
      individual,
      saving,
      percent: individual > 0
        ? Math.round(saving / individual * 100)
        : 0
    };
  }

  /*
    Allocate the native bundle plant price proportionally.
    Linked pots are added at their existing prices.
    Rounding is reconciled so the individual shares total exactly.
  */
  function plantBreakdown(bundle, selections, plants = [], config = {}) {
    const weights = selections.map((v, index) =>
      Math.max(0, Math.round(
        Number(v?.price || 0) -
        (plants[index]?.inclusivePricing ? Number(v?.potPrice || 0) : 0)
      ))
    );

    const weightTotal = weights.reduce(
      (sum, price) => sum + price,
      0
    );

    const includedCredit = config.nativeInclusiveBundle
      ? Number(config.includedPotCredit || 0)
      : 0;
    const parentPrice = Math.max(
      0,
      Math.round(Number(bundle.price || 0) - includedCredit)
    );

    const count = selections.length;

    if (!count) return [];

    const raw = weights.map(weight =>
      parentPrice * (
        weightTotal ? weight / weightTotal : 1 / count
      )
    );

    const shares = raw.map(Math.floor);

    const remainder = parentPrice - shares.reduce(
      (sum, price) => sum + price,
      0
    );

    const order = raw
      .map((price, index) => ({
        index,
        fraction: price - shares[index]
      }))
      .sort((a, b) =>
        b.fraction - a.fraction || a.index - b.index
      );

    for (let i = 0; i < remainder; i++) {
      shares[order[i % count].index] += 1;
    }

    return selections.map((variant, index) => {
      const pot = Number(variant?.potPrice || 0);
      const original = weights[index] + pot;
      const bundled = shares[index] + pot;

      return {
        original,
        bundled,
        saving: Math.max(0, original - bundled)
      };
    });
  }

  function payload(
    config,
    bundle,
    selections,
    quantity,
    pebbles,
    extraProperties = {},
    sellingPlan = ''
  ) {
    const pair =
      'SG-' +
      Date.now().toString(36).toUpperCase() +
      '-' +
      Math.random().toString(36).slice(2, 8).toUpperCase();

    const properties = {
      ...extraProperties,
      '_SG Pair': pair,
      '_SG Role': 'plant'
    };

    config.plants.forEach((plant, index) => {
      const variant = selections[index];

      const fixed = variant.options.filter(
        (value, optionIndex) =>
          optionIndex !== plant.potIndex &&
          optionIndex !== plant.colourIndex
      );

      properties['Plant ' + (index + 1)] = [
        plant.title,
        ...fixed,
        potName(plant, variant),
        colourName(plant, variant)
      ].filter(Boolean).join(' · ');
    });

    const parent = {
      id: bundle.id,
      quantity,
      properties
    };

    if (sellingPlan) parent.selling_plan = sellingPlan;

    const items = [parent];

    selections.forEach((variant, index) => {
      if (!variant.potId) return;

      const plant = config.plants[index];

      const fixed = variant.options.filter(
        (value, optionIndex) =>
          optionIndex !== plant.potIndex &&
          optionIndex !== plant.colourIndex
      );

      items.push({
        id: variant.potId,
        quantity,
        properties: {
          '_SG Pair': pair,
          '_SG Role': 'pot',
          'For Plant': config.title,
          'Included Plant': [
            plant.title,
            ...fixed
          ].filter(Boolean).join(' — '),
          'Repotting': 'Included'
        }
      });
    });

    if (pebbles.id && pebbles.quantity > 0) {
      items.push({
        id: pebbles.id,
        quantity: pebbles.quantity,
        properties: {
          '_SG Pair': pair,
          '_SG Role': 'finish',
          'For Plant': config.title
        }
      });
    }

    return { items };
  }

  window.SGBundleBuilderCore = Object.freeze({
    eligible,
    choose,
    totals,
    plantBreakdown,
    payload
  });

  class SGBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.scope =
        this.closest('.shopify-section') || this.parentElement;

      this.status = this.querySelector('[data-sgbb-status]');

      try {
        this.config = JSON.parse(
          this.querySelector('[data-sgbb-config]').textContent
        );
      } catch (_) {
        this.message(
          'The bundle options could not load. Please reload the page.'
        );
        return;
      }

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };

      this.candidates = this.config.plants.map(plant =>
        plant.variants.filter(variant =>
          eligible(plant, variant, this.config)
        )
      );

      this.selections = this.config.plants.map((plant, index) =>
        choose(plant, this.candidates[index], this.config)
      );

      this.remembered = this.config.plants.map(() => new Map());
      this.potViews = this.config.plants.map(() => new Map());
      this.previousButtons = new Map();
      this.hiddenPrices = new Set();

      this.config.plants.forEach((plant, index) =>
        this.buildPotOptions(plant, index)
      );

      this.compact = this.dataset.layout === 'compact';

      if (this.compact) {
        this.querySelectorAll('[data-sgbb-plant]').forEach((row, index) => {
          const toggle = row.querySelector('[data-sgbb-toggle]');
          const editor = row.querySelector('[data-sgbb-editor]');
          if (toggle && editor) {
            toggle.hidden = false;
            toggle.disabled = !this.candidates[index].length;
            editor.hidden = this.candidates[index].length > 0;
          }
        });
      }

      this.dataset.showDividers =
        String(this.config.showDividers);

      this.scope.classList.add('sgbb__checkout-scope');

      this.addEventListener('click', event => {
        const toggle = event.target.closest('[data-sgbb-toggle]');
        const done = event.target.closest('[data-sgbb-done]');

        if (toggle || done) {
          const row = (toggle || done).closest('[data-sgbb-plant]');
          const expanded = toggle?.getAttribute('aria-expanded') !== 'true' && !done;

          this.querySelectorAll('[data-sgbb-plant]').forEach(plantRow => {
            const control = plantRow.querySelector('[data-sgbb-toggle]');
            const editor = plantRow.querySelector('[data-sgbb-editor]');
            if (!control || !editor) return;
            const open = plantRow === row && expanded;
            editor.hidden = !open;
            control.setAttribute('aria-expanded', String(open));
          });

          if (done) row.querySelector('[data-sgbb-toggle]')?.focus();
          return;
        }

        const swatch = event.target.closest(
          '[data-sgbb-colour-id]'
        );

        const pot = event.target.closest('[data-sgbb-pot]');

        if (swatch || pot) {
          const control = swatch || pot;

          const index = Number(
            control.closest('[data-sgbb-plant]')
              .dataset.sgbbPlant
          );

          const variant = swatch
            ? this.candidates[index].find(v =>
                v.id === swatch.dataset.sgbbColourId
              )
            : this.remembered[index].get(
                pot.dataset.sgbbPot
              );

          if (variant) {
            this.selections[index] = variant;

            this.remembered[index].set(
              potName(this.config.plants[index], variant),
              variant
            );

            this.update();
          }
        }

        if (event.target.closest('[data-sgbb-add]')) {
          this.addToBag(null);
        }
      }, options);

      document.addEventListener('click', event => {
        const button = event.target.closest(
          '.product-form__cart-submit,' +
          'button[name="add"],' +
          '[name="add"][type="submit"]'
        );

        if (
          !button ||
          !(
            this.scope.contains(button) ||
            this.ownsForm(button.form)
          )
        ) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.addToBag(button.form || this.nativeForm());
      }, {
        ...options,
        capture: true
      });

      document.addEventListener('submit', event => {
        if (!this.ownsForm(event.target)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.addToBag(event.target);
      }, {
        ...options,
        capture: true
      });

      const queue = event => {
        if (!event || this.scope.contains(event.target)) {
          this.queueUpdate();
        }
      };

      document.addEventListener('change', queue, options);
      document.addEventListener('input', queue, options);

      document.addEventListener(
        'product:variant-change',
        () => this.queueUpdate(),
        options
      );

      document.addEventListener('sg:pebbles-change', event => {
        if (
          String(event.detail?.productId) ===
          this.config.productId
        ) {
          this.queueUpdate();
        }
      }, options);

      this.observer = new MutationObserver(records => {
        if (records.some(record =>
          !this.contains(record.target)
        )) {
          this.queueUpdate();
        }
      });

      this.observer.observe(this.scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value']
      });

      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);

      this.controller = null;

      this.hiddenPrices?.forEach(node =>
        node.classList.remove('sgbb__hidden-native-price')
      );

      this.previousButtons?.forEach((disabled, button) => {
        if (button.isConnected) {
          button.disabled = disabled;
        }
      });

      this.scope?.classList.remove('sgbb__checkout-scope');
    }

    queueUpdate() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;

        if (this.isConnected) this.update();
      });
    }

    nativeForm() {
      return this.scope.querySelector(
        'form[action*="/cart/add"]'
      );
    }

    ownsForm(form) {
      return Boolean(
        form?.matches?.('form[action*="/cart/add"]') &&
        this.scope.contains(form)
      );
    }

    currentBundle() {
      const input = this.nativeForm()?.querySelector(
        '[name="id"]'
      );

      const id = input
        ? String(input.value)
        : this.config.fallbackVariantId;

      return this.config.bundleVariants.find(
        variant => variant.id === id
      ) || null;
    }

    quantity(form = this.nativeForm()) {
      const input =
        form?.querySelector('[name="quantity"]') ||
        this.scope.querySelector('[name="quantity"]');

      const quantity = Number(input?.value || 1);

      return Number.isSafeInteger(quantity) && quantity > 0
        ? quantity
        : 1;
    }

    pebbles() {
      const root = this.scope.querySelector(
        '[id="sg-pebbles-' + this.config.productId + '"]'
      );

      return {
        id: root?.getAttribute(
          'data-selected-pebble-variant'
        ) || '',

        quantity: Math.max(
          0,
          Number(
            root?.getAttribute('data-selected-pebble-qty') || 0
          )
        ),

        price: Math.max(
          0,
          Number(
            root?.getAttribute('data-selected-pebble-price') || 0
          )
        ),

        available: root?.getAttribute(
          'data-selected-pebble-available'
        ) !== 'false'
      };
    }

    money(cents) {
      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: this.config.currency || 'AED',
        currencyDisplay: 'code',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(cents / 100);
    }

    message(text, success = false) {
      if (!this.status) return;

      this.status.replaceChildren();
      this.status.textContent = text;
      this.status.hidden = !text;

      this.status.setAttribute(
        'role',
        success ? 'status' : 'alert'
      );

      this.status.style.color =
        success ? 'var(--sgbb-accent)' : '';

      if (success) {
        const link = document.createElement('a');

        link.href = this.config.cartUrl;
        link.textContent = 'View bag';
        link.style.textDecoration = 'underline';
        link.style.marginInlineStart = '6px';

        this.status.appendChild(link);
      }
    }

    buildPotOptions(plant, index) {
      const row = this.querySelector(
        '[data-sgbb-plant="' + index + '"]'
      );

      const container = row.querySelector(
        '[data-sgbb-pots]'
      );

      container.replaceChildren();

      const groups = new Map();

      this.candidates[index].forEach(variant => {
        const name = potName(plant, variant);

        if (!groups.has(name)) groups.set(name, []);

        groups.get(name).push(variant);
      });

      groups.forEach((variants, name) => {
        const preferred = variants.find(v =>
          normal(colourName(plant, v)) ===
          normal(this.config.preferredColour)
        ) || variants[0];

        this.remembered[index].set(
          name,
          this.selections[index] &&
          potName(plant, this.selections[index]) === name
            ? this.selections[index]
            : preferred
        );

        const wrap = document.createElement('div');
        wrap.className = 'sgbb__pot';

        wrap.dataset.sgbbPotStyle =
          normal(name).includes('nia') ? 'nia' : 'standard';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sgbb__pot-button';
        button.dataset.sgbbPot = name;

        const picture = document.createElement('span');
        picture.className = 'sgbb__pot-image';

        const image = document.createElement('img');
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 600;
        image.height = 600;

        const placeholder = document.createElement('span');
        placeholder.className = 'sgbb__pot-placeholder';
        placeholder.textContent = name;

        picture.append(image, placeholder);

        const title = document.createElement('span');
        title.className = 'sgbb__pot-name';
        title.textContent = name;
        title.hidden = !this.config.showPotName;

        const price = document.createElement('span');
        price.className = 'sgbb__pot-price';
        price.hidden = !this.config.showPotPrice;

        button.append(picture, title, price);
        wrap.append(button);

        const swatches = [];

        if (!isNursery(name) && plant.colourIndex >= 0) {
          const group = document.createElement('div');

          group.className = 'sgbb__swatches';
          group.setAttribute('role', 'group');
          group.setAttribute(
            'aria-label',
            name + ' colours for ' + plant.title
          );

          const seen = new Set();

          variants.forEach(variant => {
            const colour = colourName(plant, variant);

            if (seen.has(colour)) return;

            seen.add(colour);

            const swatch = document.createElement('button');
            swatch.type = 'button';
            swatch.className = 'sgbb__swatch';
            swatch.dataset.sgbbColourId = variant.id;

            swatch.setAttribute(
              'aria-label',
              plant.title + ' · ' + name + ' · ' + colour
            );

            swatch.title = colour;

            const dot = document.createElement('span');
            dot.className = 'sgbb__swatch-dot';

            dot.style.backgroundColor =
              variant.swatchColor ||
              this.fallbackColour(colour);

            if (variant.swatchImage) {
              dot.style.backgroundImage =
                'url(' +
                JSON.stringify(variant.swatchImage) +
                ')';
            }

            swatch.append(dot);
            group.append(swatch);
            swatches.push(swatch);
          });

          wrap.append(group);
        }

        const colour = document.createElement('p');
        colour.className = 'sgbb__colour-name';
        colour.hidden = !this.config.showColourName;

        wrap.append(colour);
        container.append(wrap);

        this.potViews[index].set(name, {
          wrap,
          button,
          image,
          placeholder,
          price,
          swatches,
          colour
        });
      });

      const error = row.querySelector(
        '[data-sgbb-plant-error]'
      );

      if (error) {
        error.hidden = groups.size > 0;
        error.textContent = this.config.unavailableLabel;
      }
    }

    fallbackColour(value) {
      const name = normal(value);

      for (const [word, colour] of Object.entries({
        white: '#fff',
        black: '#171717',
        beige: '#d4c5aa',
        cream: '#eee7d7',
        grey: '#999994',
        gray: '#999994',
        brown: '#8b6b55',
        olive: '#6b7342',
        maroon: '#800000',
        blue: '#365f91',
        green: '#5a6f4e',
        red: '#8d3936'
      })) {
        if (name.includes(word)) return colour;
      }

      return '#d8d6cf';
    }

    renderPlant(plant, index) {
      const selected = this.selections[index];

      const row = this.querySelector(
        '[data-sgbb-plant="' + index + '"]'
      );

      if (!row) return;

      const includedVariant = plant.variants.find(v =>
        String(v.id) === String(plant.defaultId)
      );

      const includedPotPrice = Number(
        includedVariant?.potPrice || 0
      );

      if (this.compact && selected) {
        const summary = row.querySelector('[data-sgbb-selected-pot]');
        const delta = row.querySelector('[data-sgbb-selected-delta]');
        const difference = Number(selected.potPrice || 0) - includedPotPrice;

        if (summary) {
          summary.textContent = [potName(plant, selected), colourName(plant, selected)]
            .filter(Boolean).join(' · ');
        }

        if (delta) {
          delta.textContent = difference === 0
            ? this.config.compactLabels.included
            : (difference > 0 ? '+ ' : '− ') + this.money(Math.abs(difference));
        }
      }

      this.potViews[index].forEach((view, name) => {
        const variant = this.remembered[index].get(name);

        if (!variant) return;

        const active = Boolean(
          selected &&
          potName(plant, selected) === name
        );

        view.wrap.dataset.selected = String(active);

        view.button.setAttribute(
          'aria-pressed',
          String(active)
        );

        view.button.setAttribute(
          'aria-label',
          plant.title + ' · ' + name +
          (
            colourName(plant, variant)
              ? ' · ' + colourName(plant, variant)
              : ''
          )
        );

        const selectedPotPrice = Number(
          variant.potPrice || 0
        );

        const difference =
          selectedPotPrice - includedPotPrice;

        view.price.dataset.included =
          String(difference === 0);

        view.price.textContent = difference === 0
          ? 'Included'
          : (
              difference > 0 ? '+ ' : '− '
            ) + this.money(Math.abs(difference));

        view.image.alt = name +
          (
            colourName(plant, variant)
              ? ' · ' + colourName(plant, variant)
              : ''
          );

        view.image.hidden = !variant.potImage;
        view.placeholder.hidden = Boolean(variant.potImage);

        if (
          variant.potImage &&
          view.image.getAttribute('src') !== variant.potImage
        ) {
          view.image.src = variant.potImage;
        }

        view.swatches.forEach(button => {
          button.setAttribute(
            'aria-pressed',
            String(
              active &&
              button.dataset.sgbbColourId === selected.id
            )
          );
        });

        view.colour.textContent =
          colourName(plant, variant);
      });

      const note = row.querySelector(
        '[data-sgbb-repotting-note]'
      );

      if (note) {
        note.hidden =
          !this.config.showIncludedNote ||
          !selected?.potId;
      }

      const pricing = row.querySelector(
        '[data-sgbb-plant-pricing]'
      );

      const price = this.plantPricing?.[index];

      if (pricing) {
        pricing.hidden =
          !this.config.showPrice || !price;

        if (price) {
          pricing.querySelector(
            '[data-sgbb-plant-original]'
          ).textContent = this.money(price.original);

          const saving = pricing.querySelector(
            '[data-sgbb-plant-saving]'
          );

          saving.hidden =
            !this.config.showSavings ||
            price.saving <= 0;

          saving.textContent =
            'You save ' + this.money(price.saving);
        }
      }

      if (selected) {
        const link = row.querySelector(
          '[data-sgbb-plant-link]'
        );

        if (link) {
          link.href =
            plant.url + '?variant=' + selected.id;

          const image = link.querySelector('img');

          const source =
            selected.image || plant.defaultImage;

          if (
            image &&
            source &&
            image.getAttribute('src') !== source
          ) {
            image.removeAttribute('srcset');
            image.src = source;
          }

          if (image && this.compact) {
            image.alt = [plant.title, potName(plant, selected), colourName(plant, selected)]
              .filter(Boolean).join(' · ');
          }
        }
      }
    }

    addButtons() {
      return [
        ...this.scope.querySelectorAll(
          '.product-form__cart-submit,' +
          'button[name="add"],' +
          '[name="add"][type="submit"]'
        ),
        this.querySelector('[data-sgbb-add]')
      ].filter(Boolean);
    }

    update() {
      const bundle = this.currentBundle();
      const pebbles = this.pebbles();

      const valid = Boolean(
        bundle?.available &&
        this.selections.every(Boolean) &&
        (
          !pebbles.id ||
          pebbles.quantity <= 0 ||
          pebbles.available
        )
      );

      this.plantPricing =
        bundle && this.selections.every(Boolean)
          ? plantBreakdown(bundle, this.selections, this.config.plants, this.config)
          : [];

      this.config.plants.forEach((plant, index) =>
        this.renderPlant(plant, index)
      );

      const priceArea = this.querySelector(
        '[data-sgbb-price-area]'
      );

      if (priceArea) {
        priceArea.hidden =
          !this.config.showPrice || !bundle;
      }

      if (bundle && this.config.showPrice) {
        const quantity = this.quantity();

        const price = totals(
          bundle,
          this.selections,
          quantity,
          pebbles,
          this.config.plants,
          this.config
        );

        const priceNode = this.querySelector(
          '[data-sgbb-price]'
        );

        if (priceNode) {
          priceNode.textContent = this.money(price.total);
        }

        if (this.compact) {
          this.scope.querySelectorAll('[data-sgbb-summary-price]').forEach(node => {
            const formatted = this.money(price.total);
            if (node.textContent !== formatted) node.textContent = formatted;
          });
        }

        const comparison = this.querySelector(
          '[data-sgbb-comparison]'
        );

        const saving = this.querySelector(
          '[data-sgbb-saving]'
        );

        const showSaving =
          this.config.showSavings &&
          valid &&
          price.saving > 0;

        if (comparison) {
          comparison.hidden = !showSaving;
          comparison.textContent =
            this.money(price.individual);
        }

        if (saving) {
          saving.hidden = !showSaving;

          saving.textContent =
            this.config.saveLabel + ' ' +
            this.money(price.saving) +
            (
              this.dataset.layout !== 'compact' && price.percent > 0
                ? ' · ' + price.percent + '%'
                : ''
            );
        }

        const comparisonLabel = this.querySelector(
          '[data-sgbb-comparison-label]'
        );

        if (comparisonLabel) {
          comparisonLabel.hidden = !showSaving;
        }

        const totalNote = this.querySelector(
          '[data-sgbb-total-note]'
        );

        if (totalNote) {
          totalNote.hidden =
            quantity === 1 &&
            !(pebbles.id && pebbles.quantity > 0);

          totalNote.textContent =
            'Total for ' + quantity +
            (
              quantity === 1
                ? ' bundle'
                : ' bundles'
            ) +
            (
              pebbles.id && pebbles.quantity > 0
                ? ' + selected pebbles'
                : ''
            );
        }

        this.scope.querySelectorAll(
          '.product__price,' +
          '[data-product-price],' +
          '.product-price'
        ).forEach(node => {
          if (!this.contains(node)) {
            this.hiddenPrices.add(node);
            node.classList.add(
              'sgbb__hidden-native-price'
            );
          }
        });
      }

      const own = this.querySelector('[data-sgbb-add]');

      if (own) {
        own.hidden = this.addButtons().some(
          button => button !== own
        );
      }

      this.addButtons().forEach(button => {
        if (!this.previousButtons.has(button)) {
          this.previousButtons.set(
            button,
            button.disabled
          );
        }

        button.disabled = this.adding || !valid;
        button.setAttribute('aria-disabled', String(this.adding || !valid));

        if (this.compact && !this.adding && valid) {
          const text = button.querySelector('[data-add-to-cart-text]');
          if (text && text.textContent.trim() !== this.config.compactLabels.add) {
            text.textContent = this.config.compactLabels.add;
          }
          button.setAttribute('aria-label', this.config.compactLabels.add);
          button.dataset.langAvailable = this.config.compactLabels.add;
        }
      });

      if (!this.selections.every(Boolean)) {
        this.message(this.config.unavailableLabel);
      } else if (!bundle?.available) {
        this.message(
          'This bundle is currently unavailable.'
        );
      } else if (
        pebbles.id &&
        pebbles.quantity > 0 &&
        !pebbles.available
      ) {
        this.message(
          'The selected pebbles are currently unavailable.'
        );
      } else if (this.validationError) {
        this.validationError = false;
        this.message('');
      }

      this.validationError = !valid;

      this.dispatchEvent(
        new CustomEvent('sg:bundle-selection-change', {
          bubbles: true,
          detail: {
            productId: this.config.productId,
            valid,
            selections: this.selections.map(v =>
              v
                ? {
                    variantId: v.id,
                    potVariantId: v.potId
                  }
                : null
            )
          }
        })
      );
    }

    async addToBag(form) {
      if (this.adding) return;

      const bundle = this.currentBundle();
      const pebbles = this.pebbles();

      if (
        !bundle?.available ||
        this.selections.some(v => !v) ||
        (
          pebbles.id &&
          pebbles.quantity > 0 &&
          !pebbles.available
        )
      ) {
        this.update();
        return;
      }

      form = form || this.nativeForm();

      if (form && !form.reportValidity()) return;

      const data = form ? new FormData(form) : null;

      const extra = data
        ? Object.fromEntries(
            [...data.entries()]
              .filter(([key, value]) =>
                /^properties\[.*\]$/.test(key) &&
                typeof value === 'string'
              )
              .map(([key, value]) => [
                key.slice(11, -1),
                value
              ])
          )
        : {};

      const request = payload(
        this.config,
        bundle,
        this.selections,
        this.quantity(form),
        pebbles,
        extra,
        data?.get('selling_plan') || ''
      );

      this.adding = true;
      this.message('');

      const labels = this.addButtons().map(button => ({
        button,
        html: button.innerHTML
      }));

      labels.forEach(({ button }) => {
        button.disabled = true;
        button.textContent = this.config.addingLabel;
      });

      try {
        const response = await fetch(this.config.addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(request)
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            result.message ||
            'Unable to add this bundle to your bag.'
          );
        }

        document.dispatchEvent(
          new CustomEvent('apps:product-added-to-cart', {
            detail: {
              productId: this.config.productId,
              items: result.items || request.items
            }
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:refresh')
        );

        if (this.config.redirect) {
          window.location.assign(this.config.cartUrl);
        } else if (this.isConnected) {
          this.message(
            'Your bundle has been added.',
            true
          );
        }
      } catch (error) {
        if (this.isConnected) {
          this.message(
            error.message ||
            'Unable to add this bundle to your bag.'
          );
        }
      } finally {
        this.adding = false;

        labels.forEach(({ button, html }) => {
          if (button.isConnected) {
            button.innerHTML = html;
          }
        });

        if (this.isConnected) this.update();
      }
    }
  }

  customElements.define(
    'sg-bundle-builder',
    SGBundleBuilder
  );
})();
